import { afterNextRender, Component, computed, DestroyRef, ElementRef, inject, input, output, signal } from '@angular/core';
import { sankey, sankeyLinkHorizontal, sankeyRight } from 'd3-sankey';
import type { SankeyGraph } from 'd3-sankey';

/**
 * Generic, N-stage Sankey flow-map chart - pure "nodes/links in -> SVG + events out".
 *
 * It knows nothing about traffic, services, or destinations: consumers hand it
 * already-stage-assigned nodes/links plus column headers, and it runs d3-sankey,
 * renders the SVG, does hover-isolate, and emits node/link click events. All domain
 * meaning (what a node is, what a click does, tooltip text) lives in the consumer's
 * adapter and its event handlers.
 *
 * Columns come from `stageIndex` (0..N-1); links must connect adjacent-stage nodes
 * (d3 derives the column layout from that topology). `kind` and `state` are opaque
 * styling strings the chart maps to generic CSS classes (`node-kind-<kind>`,
 * `link-state-<state>`, `link-touches-<kind>`) - no chart-side interpretation.
 *
 * Ported from the nf-console-redesign SankeyChartComponent; re-themed to the house
 * design tokens (see the component CSS).
 */

/** One column node. `stageIndex` = which column (0-based). `kind`/`title`/
 *  `ariaLabel` are opaque, consumer-set. */
export interface SankeyChartNode {
    id: string;
    stageIndex: number;
    label: string;
    /** Tooltip text, rendered verbatim (<title>). Falls back to `label`. */
    title?: string;
    /** Opaque styling tag -> `node-kind-<kind>` CSS class (e.g. 'catch-all'). */
    kind?: string;
    /** Optional magnitude; unused by the layout today (d3 sizes nodes from links). */
    weight?: number;
    /** Opaque a11y label for the node's clickable group. */
    ariaLabel?: string;
    /** When true, the node shows a leading expand/collapse pill (a chevron plus `expandCount`) and emits
     *  `nodeToggle` when clicked; the consumer rebuilds the graph with the drilled-in detail. Used for the
     *  collapse-to-gateways / drill-down-to-addresses flow map. */
    expandable?: boolean;
    /** How many hidden children the pill advertises while collapsed (shown as the count beside the chevron). */
    expandCount?: number;
    /** Whether this node is currently expanded (chevron points down, no count); set by the consumer. */
    expanded?: boolean;
    /** Optional secondary label shown under this node's name when service names are on (e.g. the service(s)
     *  serving a concrete endpoint). Rendered only for the last column, as a quieter second line. */
    serviceLabel?: string;
}

/** One ribbon between two adjacent-stage nodes. `state` is an opaque styling tag. */
export interface SankeyChartLink {
    id: string;
    sourceNodeId: string;
    targetNodeId: string;
    weight: number;
    /** Opaque styling tag -> `link-state-<state>` CSS class (e.g. 'full'/'partial'). */
    state?: string;
    /** Opaque a11y label for the ribbon. */
    ariaLabel?: string;
    /** Optional label drawn on the ribbon (e.g. the service it carries) when the consumer turns labels on. */
    serviceLabel?: string;
}

export interface SankeyNodeClickEvent {
    nodeId: string;
    stageIndex: number;
    kind?: string;
    /** The click was made with Ctrl (Windows) or Cmd (Mac) held, to add to an existing selection. */
    aggregate?: boolean;
}
export interface SankeyLinkClickEvent {
    linkId: string;
    sourceNodeId: string;
    targetNodeId: string;
    /** The click was made with Ctrl (Windows) or Cmd (Mac) held, to add to an existing selection. */
    aggregate?: boolean;
}
/** An expand/collapse pill was activated. `expanded` is the state being requested (the opposite of the
 *  node's current state). */
export interface SankeyNodeToggleEvent {
    nodeId: string;
    stageIndex: number;
    expanded: boolean;
}

// --- layout constants (generic; wide viewBox so columns fill the card) ---
const SANK_W = 1900;
const SANK_MARGIN_X = 168; // room for first/last-column labels outside the columns
const SANK_MARGIN_L_MIN = 40; // smallest left margin, so even a 1-char leftmost label keeps a gutter
const LABEL_CHAR_W = 7.6; // per-character width fallback when canvas text measurement is unavailable
const LABEL_PAD = 14; // label-to-node gap plus a small outer gutter, added to the measured label width
const SANK_MARGIN_Y = 18;
const SANK_NODE_W = 14;
const SANK_NODE_PAD = 16;
const SANK_HEADER_H = 30; // reserved band at top for the per-column headers
// Desired on-SCREEN sizes, held constant across chart widths (the SVG scales to its container, so plain
// viewBox sizes would balloon at full width and shrink when the side drawer narrows the chart). The label
// margins are scaled the same way so the (now larger, when narrow) labels still have room and do not clip.
const HEADER_TARGET_PX = 11;
const NODE_LABEL_TARGET_PX = 13;
// A node's optional second line (the service names under an endpoint address): a touch smaller than the
// name so it reads as secondary. Its width is this fraction of the name's at the same text.
const NODE_SUBLABEL_TARGET_PX = 11;
const SUBLABEL_WIDTH_RATIO = NODE_SUBLABEL_TARGET_PX / NODE_LABEL_TARGET_PX;
// Longest service sub-label kept in full; beyond this it truncates, so the right margin stays bounded.
const SUBLABEL_MAX_CHARS = 54;
// The right margin may grow past SANK_MARGIN_X to seat a long service sub-label, but no further than this,
// so one very long name cannot crush the columns (it truncates instead).
const SANK_MARGIN_R_MAX = 380;
// On-screen width of the node-label halo (the readability outline drawn behind the text), total across both
// sides, sized like the font so it stays constant as the chart scales.
const LABEL_HALO_PX = 3;
// A ribbon must be at least this thick on screen to seat a service label, else the label is skipped.
const SVC_LABEL_MIN_PX = 11;

// --- expand pill geometry (drawn on expandable nodes, on their leading/left side) ---
const PILL_W = 30; // pill width (fits a chevron + a 1-2 digit count)
const PILL_H = 17;
const PILL_GAP = 7; // gap between the pill's right edge and the node's left edge
const NAME_GAP = 9; // gap between the node's name and the pill
const CHEV_S = 3; // chevron half-size
const PILL_TXT_PX = 10; // on-SCREEN size of the pill's count text
// These pill sizes are base values; like the labels they are divided by displayScale so the pill stays a
// constant size on screen instead of shrinking when the side drawer narrows the chart.
// Extra left margin so an expandable node's name + pill fit outside the first column without clipping.
const SANK_MARGIN_L_EXPANDABLE = 236;

// --- d3 input shapes (extra props ride alongside d3's geometry) ---
interface NodeX {
    id: string;
    stageIndex: number;
    label: string;
    title: string;
    kind?: string;
    ariaLabel?: string;
    expandable?: boolean;
    expandCount?: number;
    expanded?: boolean;
    serviceLabel?: string;
}
interface LinkX {
    linkId: string;
    state?: string;
    ariaLabel?: string;
    serviceLabel?: string;
    /** kinds of this link's endpoint nodes -> `link-touches-<kind>` classes. */
    touchesKinds: string[];
}

/** A positioned node the template renders (d3 geometry flattened out). */
interface NodeView extends NodeX {
    x0: number;
    y0: number;
    w: number;
    h: number;
    isFirst: boolean;
    isLast: boolean;
    /** The service sub-label actually shown under the name, already truncated to fit the right margin. */
    serviceLabelShown?: string;
}
/** A positioned ribbon the template renders. */
interface LinkView {
    key: string;
    linkId: string;
    sourceId: string;
    targetId: string;
    path: string;
    width: number;
    state?: string;
    ariaLabel?: string;
    serviceLabel?: string;
    /** Midpoint of the ribbon, where the optional service label is centered. */
    labelX: number;
    labelY: number;
    touchesKinds: string[];
}
/** A column header centered over its stage's node column. */
interface HeaderView {
    x: number;
    y: number;
    text: string;
}

@Component({
    selector: 'app-flow-map-chart',
    standalone: true,
    templateUrl: './flow-map-chart.component.html',
    styleUrls: ['./flow-map-chart.component.css'],
})
export class FlowMapChartComponent {
    // --- inputs (already stage-assigned) ---
    readonly nodes = input<readonly SankeyChartNode[]>([]);
    readonly links = input<readonly SankeyChartLink[]>([]);
    /** Column headers by stageIndex; a missing/empty entry renders no header. */
    readonly headers = input<readonly string[]>([]);
    /** Node ordering within each column: 'usage' = circuit volume (descending), 'alpha' = by label. */
    readonly sortBy = input<'usage' | 'alpha'>('usage');
    /** Draw each ribbon's serviceLabel at its midpoint (when the consumer sets one on the link). */
    readonly showServices = input<boolean>(false);
    /** Accessible name for the whole chart svg. */
    readonly ariaLabel = input<string>('Traffic flow map');

    // --- outputs (meaning lives in the consumer) ---
    readonly nodeClick = output<SankeyNodeClickEvent>();
    readonly linkClick = output<SankeyLinkClickEvent>();
    /** Empty-canvas click - consumers may use it to clear a selection. */
    readonly backgroundClick = output<void>();
    /** A node was hovered (its event), or the pointer left all nodes (null). Consumers resolve the real
     *  path and feed it back through the highlight inputs - the chart cannot trace a path on its own,
     *  because a shared middle node loses which source reaches which endpoint. */
    readonly nodeHover = output<SankeyNodeClickEvent | null>();
    /** An expandable node's pill was activated: the consumer rebuilds the graph with that node drilled in
     *  (or collapsed back). The chart only renders the affordance; the expand/collapse data logic lives in
     *  the consumer's adapter. */
    readonly nodeToggle = output<SankeyNodeToggleEvent>();

    // --- path highlight (consumer-driven): when either set is non-empty, every node/ribbon NOT in it is
    // dimmed so the given path stands out. ---
    readonly highlightNodeIds = input<readonly string[]>([]);
    readonly highlightLinkIds = input<readonly string[]>([]);
    private readonly highlightNodeSet = computed(() => new Set(this.highlightNodeIds()));
    private readonly highlightLinkSet = computed(() => new Set(this.highlightLinkIds()));
    private readonly highlightActive = computed(
        () => this.highlightNodeSet().size > 0 || this.highlightLinkSet().size > 0
    );

    // --- emphasis (consumer-driven): an accent overlay drawn ON TOP of the base highlight (the lit path is
    // left as-is; these nodes/ribbons additionally get an accent color). Used to pick out a sub-path, e.g.
    // the breakdown rows the viewer checked, without dimming the rest of the lit flow. ---
    readonly emphasisNodeIds = input<readonly string[]>([]);
    readonly emphasisLinkIds = input<readonly string[]>([]);
    /** Per-ribbon selected share (display link id -> 0..1): the emphasis band is drawn at this fraction of
     *  the ribbon's width, so a selection shows as its real proportion of each ribbon. Missing = full width. */
    readonly emphasisLinkFractions = input<Record<string, number>>({});
    /** The same per-ribbon share for the hover accent, so hovering shows its real proportion of each ribbon. */
    readonly hoverLinkFractions = input<Record<string, number>>({});
    private readonly emphasisNodeSet = computed(() => new Set(this.emphasisNodeIds()));
    private readonly emphasisLinkSet = computed(() => new Set(this.emphasisLinkIds()));

    // --- hover accent (consumer-driven): a SOFTER overlay than emphasis, additive on top of it, for the
    // breakdown row the pointer is over. Distinct and lighter so a transient hover reads apart from the
    // committed selection. ---
    readonly hoverNodeIds = input<readonly string[]>([]);
    readonly hoverLinkIds = input<readonly string[]>([]);
    private readonly hoverNodeSet = computed(() => new Set(this.hoverNodeIds()));
    private readonly hoverLinkSet = computed(() => new Set(this.hoverLinkIds()));
    private readonly hoverActive = computed(() => this.hoverNodeSet().size > 0 || this.hoverLinkSet().size > 0);

    // --- pan/zoom: scroll the wheel to zoom toward the cursor, drag to pan, double-click to reset. The
    // whole chart (headers + ribbons + nodes) rides a single transform group; the background and defs stay
    // put so empty-space clicks and the hatch pattern are unaffected. The fitted view is k=1; the user can
    // zoom out below it (down to minK) to see the whole diagram with margin, and double-click resets to fit. ---
    private readonly minK = 0.5;
    private readonly maxK = 8;
    readonly view = signal<{ k: number; x: number; y: number }>({ k: 1, x: 0, y: 0 });
    readonly transform = computed(() => {
        const v = this.view();
        return `translate(${v.x},${v.y}) scale(${v.k})`;
    });
    /** True while a pan drag is in progress (drives the grabbing cursor). */
    readonly panning = signal(false);
    // drag anchor, in the svg's viewBox coordinates, plus the pan offset at press time
    private dragStart: { px: number; py: number; vx: number; vy: number } | null = null;
    // where the press began in client pixels, to tell a pan from a click
    private downClient: { x: number; y: number } | null = null;
    // set once a press has moved far enough to count as a drag, so the trailing click is ignored
    private suppressClick = false;

    // --- available container size: measured live (the content box of the chart's parent) so the chart height
    // fills the space instead of leaving a vertical gap below it. The viewBox width stays fixed (SANK_W), so
    // only the height adapts - the horizontal layout, margins and narrow-screen behavior are unchanged. The
    // defaults keep the first paint sane until the observer fires. ---
    private readonly availW = signal(SANK_W);
    private readonly availH = signal(620);
    /** The SVG's on-screen scale (display width / viewBox width), floored so an unusually narrow chart does
     *  not blow the viewBox sizes up without bound. Used to keep header/label fonts and the label margins a
     *  constant size on screen. */
    private readonly displayScale = computed(() => Math.max(0.45, Math.min(1, (this.availW() || SANK_W) / SANK_W)));
    /** Header / node-label font sizes in viewBox units, so each renders at its constant on-screen target. */
    readonly headerFontSize = computed(() => HEADER_TARGET_PX / this.displayScale());
    readonly labelFontSize = computed(() => NODE_LABEL_TARGET_PX / this.displayScale());
    /** The endpoint service sub-label's font size in viewBox units, held constant on screen like the name. */
    readonly subLabelFontSize = computed(() => NODE_SUBLABEL_TARGET_PX / this.displayScale());
    /** Node-label halo stroke width in viewBox units, so the readability outline stays a constant size on
     *  screen like the label text. */
    readonly labelHaloWidth = computed(() => LABEL_HALO_PX / this.displayScale());
    /** Minimum ribbon width (viewBox units) to draw a service label on, held constant on screen. */
    readonly svcLabelMinWidth = computed(() => SVC_LABEL_MIN_PX / this.displayScale());
    private readonly el = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        // Observe the parent's content box (excludes its padding) rather than the host, whose height is set
        // by the svg and would feed back on itself. The flow/coverage containers give the parent a definite
        // height, so this settles in one pass.
        afterNextRender(() => {
            const host = this.el.nativeElement as HTMLElement;
            const target = host.parentElement ?? host;
            const ro = new ResizeObserver((entries) => {
                const box = entries[0]?.contentRect;
                if (!box || box.width === 0 || box.height === 0) return;
                this.availW.set(box.width);
                this.availH.set(box.height);
            });
            ro.observe(target);
            this.destroyRef.onDestroy(() => ro.disconnect());
        });
    }

    /**
     * d3-sankey layout over the generic nodes/links. Each column is derived from
     * link topology (d3's default justify align), which agrees with `stageIndex`
     * when consumers link only adjacent stages. Geometry is flattened into plain
     * view shapes so the template imports no d3.
     */
    readonly layout = computed<{ nodes: NodeView[]; links: LinkView[]; headers: HeaderView[]; width: number; height: number }>(
        () => {
            const inNodes = this.nodes();
            const inLinks = this.links();
            if (inNodes.length === 0) return { nodes: [], links: [], headers: [], width: SANK_W, height: 200 };

            const stageCount = Math.max(...inNodes.map((n) => n.stageIndex)) + 1;
            const kindById = new Map(inNodes.map((n) => [n.id, n.kind]));
            // Expandable nodes carry their name + pill to their left. That only pushes past the chart's left
            // edge when an expandable node is in the LEFTMOST column; when an address column sits to its left,
            // the name + pill land in the gap between columns and need no extra outer margin. So reserve the
            // wider left margin only when the leftmost present column is itself expandable - otherwise it is
            // dead space on the left. The side margins hold the node labels, and the labels are kept a constant
            // on-screen size, so the margins are scaled the same way (bigger in viewBox units when the chart is
            // narrow) - otherwise the larger labels would overrun a fixed margin and clip.
            const minStage = Math.min(...inNodes.map((n) => n.stageIndex));
            const leftCol = inNodes.filter((n) => n.stageIndex === minStage);
            const leftColExpandable = leftCol.some((n) => n.expandable);
            // When the leftmost column is plain labels (no expand pill), the generic SANK_MARGIN_X is sized for
            // the widest column (the long host-address labels on the right) and leaves dead space next to short
            // labels like source IPs. Fit the left margin to the longest label actually in that column instead -
            // clamped to a small floor so there is still a gutter, and never wider than the generic margin.
            const widestLeftLabel = Math.max(0, ...leftCol.map((n) => this.measureLabelWidth(this.truncLabel(n.label))));
            const fitMarginL = Math.min(SANK_MARGIN_X, Math.max(SANK_MARGIN_L_MIN, widestLeftLabel + LABEL_PAD));
            const s = this.displayScale();
            const marginL = (leftColExpandable ? SANK_MARGIN_L_EXPANDABLE : fitMarginL) / s;
            // Right margin holds the last column's labels. Grow it past the generic margin when service names
            // are on and an endpoint's service sub-label is wider than its address, so the names are not cut
            // off; capped so one long name truncates rather than crushing the columns.
            const maxStage = Math.max(...inNodes.map((n) => n.stageIndex));
            const rightCol = inNodes.filter((n) => n.stageIndex === maxStage);
            const showSvc = this.showServices();
            const widestRight = Math.max(
                0,
                ...rightCol.map((n) => {
                    const nameW = this.measureLabelWidth(this.truncLabel(n.label));
                    const svcW =
                        showSvc && n.serviceLabel
                            ? this.measureLabelWidth(this.truncLabel(n.serviceLabel, SUBLABEL_MAX_CHARS)) *
                              SUBLABEL_WIDTH_RATIO
                            : 0;
                    return Math.max(nameW, svcW);
                })
            );
            const marginR = Math.min(SANK_MARGIN_R_MAX, Math.max(SANK_MARGIN_X, widestRight + LABEL_PAD)) / s;

            // tallest column drives the height
            let maxCol = 0;
            for (let s = 0; s < stageCount; s += 1) {
                const c = inNodes.filter((n) => n.stageIndex === s).length;
                if (c > maxCol) maxCol = c;
            }
            // Fill the container's height: the viewBox scales to the container width, so matching the
            // container's aspect (SANK_W * availH/availW) makes the displayed chart exactly fill the available
            // height - no empty gutter below. The node-count floor keeps a dense column readable: if it needs
            // more height than the fill, the chart grows past the container and scrolls instead of squishing.
            // The fill is capped so an unusually tall or indefinite-height container can't balloon the chart.
            const nodeFloor = maxCol * (SANK_NODE_PAD + 22) + SANK_MARGIN_Y * 2 + SANK_HEADER_H;
            const aw = this.availW();
            const fillHeight = aw > 0 ? Math.min(1400, (SANK_W * this.availH()) / aw) : 620;
            const height = Math.max(nodeFloor, fillHeight);

            const gen = sankey<NodeX, LinkX>()
                .nodeId((d) => d.id)
                .nodeWidth(SANK_NODE_W)
                .nodePadding(SANK_NODE_PAD)
                // Align columns by distance from the sink (right-align). This keeps same-depth nodes in one
                // column even when some have no incoming link, so a collapsed gateway (no source-address
                // feeding it) still lines up with an expanded one, and drilled-in addresses ragged out to the
                // left. With every column present this matches the default layout.
                .nodeAlign(sankeyRight)
                // Fixed within-column order: by circuit volume (value) or by label. localeCompare's numeric
                // mode orders IP labels naturally (10.1.1.2 before 10.1.1.10).
                .nodeSort(
                    this.sortBy() === 'alpha'
                        ? (a, b) => `${a.label ?? ''}`.localeCompare(`${b.label ?? ''}`, undefined, { numeric: true })
                        : (a, b) => (b.value ?? 0) - (a.value ?? 0)
                )
                .extent([
                    // top edge pushed down by the header band so headers sit above row 1
                    [marginL, SANK_MARGIN_Y + SANK_HEADER_H],
                    [SANK_W - marginR, height - SANK_MARGIN_Y],
                ]);

            // d3 mutates its input graph; feed fresh copies each run.
            const graph: SankeyGraph<NodeX, LinkX> = gen({
                nodes: inNodes.map((n) => ({
                    id: n.id,
                    stageIndex: n.stageIndex,
                    label: n.label,
                    title: n.title ?? n.label,
                    kind: n.kind,
                    ariaLabel: n.ariaLabel,
                    expandable: n.expandable,
                    expandCount: n.expandCount,
                    expanded: n.expanded,
                    serviceLabel: n.serviceLabel,
                })),
                links: inLinks.map((l) => ({
                    source: l.sourceNodeId,
                    target: l.targetNodeId,
                    value: l.weight,
                    linkId: l.id,
                    state: l.state,
                    ariaLabel: l.ariaLabel,
                    serviceLabel: l.serviceLabel,
                    touchesKinds: [kindById.get(l.sourceNodeId), kindById.get(l.targetNodeId)].filter(
                        (k): k is string => !!k
                    ),
                })),
            } as unknown as SankeyGraph<NodeX, LinkX>);

            const linkPath = sankeyLinkHorizontal<NodeX, LinkX>();

            // First/last by actual column x, not stageIndex: under drill-down the source-address column
            // (stage 0) can be absent, so the leftmost-present column is whatever d3 placed furthest left.
            // With every column present this agrees with stageIndex.
            const xs = graph.nodes.map((n) => n.x0 ?? 0);
            const minX0 = Math.min(...xs);
            const maxX0 = Math.max(...xs);

            const nodeViews: NodeView[] = graph.nodes.map((n) => {
                const isLast = (n.x0 ?? 0) === maxX0;
                return {
                    id: n.id,
                    stageIndex: n.stageIndex,
                    label: n.label,
                    title: n.title,
                    kind: n.kind,
                    ariaLabel: n.ariaLabel,
                    expandable: n.expandable,
                    expandCount: n.expandCount,
                    expanded: n.expanded,
                    serviceLabel: n.serviceLabel,
                    x0: n.x0 ?? 0,
                    y0: n.y0 ?? 0,
                    w: (n.x1 ?? 0) - (n.x0 ?? 0),
                    h: (n.y1 ?? 0) - (n.y0 ?? 0),
                    isFirst: (n.x0 ?? 0) === minX0,
                    isLast,
                    // Only the last column shows the service sub-label, truncated to the bounded right margin.
                    serviceLabelShown: isLast && n.serviceLabel ? this.truncLabel(n.serviceLabel, SUBLABEL_MAX_CHARS) : undefined,
                };
            });

            const linkViews: LinkView[] = graph.links.map((l) => {
                // d3-sankey adds the x/y geometry to the endpoint nodes; NodeX itself does not declare it.
                const s = l.source as NodeX & { x0?: number; x1?: number };
                const t = l.target as NodeX & { x0?: number; x1?: number };
                return {
                    key: `${s.id} ${t.id}`,
                    linkId: l.linkId,
                    sourceId: s.id,
                    targetId: t.id,
                    path: linkPath(l) ?? '',
                    width: Math.max(1, l.width ?? 1),
                    state: l.state,
                    ariaLabel: l.ariaLabel,
                    serviceLabel: l.serviceLabel,
                    // Ribbon midpoint: halfway across the gap between the two nodes, centered on the band.
                    labelX: ((s.x1 ?? 0) + (t.x0 ?? 0)) / 2,
                    labelY: ((l.y0 ?? 0) + (l.y1 ?? 0)) / 2,
                    touchesKinds: l.touchesKinds,
                };
            });

            // column headers: centered over each stage's node x-range (same coords the
            // nodes use, so they track the columns under the responsive viewBox).
            const headerY = SANK_MARGIN_Y + 15;
            const headerText = this.headers();
            const headerViews: HeaderView[] = [];
            for (let s = 0; s < stageCount; s += 1) {
                const text = headerText[s];
                if (!text) continue;
                const col = nodeViews.filter((n) => n.stageIndex === s);
                if (col.length === 0) continue;
                const x0 = Math.min(...col.map((n) => n.x0));
                headerViews.push({ x: x0 + col[0].w / 2, y: headerY, text });
            }

            return { nodes: nodeViews, links: linkViews, headers: headerViews, width: SANK_W, height };
        }
    );

    // --- template class helpers (generic kind/state -> CSS class strings) ---
    nodeGroupClass(n: NodeView): string {
        const pos = n.isFirst ? 'pos-first' : n.isLast ? 'pos-last' : 'pos-interior';
        const kind = n.kind ? ` node-kind-${n.kind}` : '';
        return `sankey-node-g ${pos} col-${n.stageIndex} node-clickable${kind}`;
    }
    linkClass(l: LinkView): string {
        const state = l.state ? ` link-state-${l.state}` : '';
        const touches = l.touchesKinds.map((k) => ` link-touches-${k}`).join('');
        return `sankey-link${state}${touches}`;
    }
    /** Dim a ribbon. While a breakdown row is HOVERED, spotlight it: dim everything off the hovered flow
     *  (including the other selected/emphasized paths, so the hovered one stands out among them). Otherwise,
     *  while a path is highlighted, dim what is off it - but keep the emphasized (selected) segments bright,
     *  even when they sit off the clicked element's path (e.g. an auto-revealed source address). */
    isLinkDim(l: LinkView): boolean {
        if (this.hoverActive()) {
            return !this.hoverLinkSet().has(l.linkId);
        }
        return this.highlightActive() && !this.highlightLinkSet().has(l.linkId) && !this.emphasisLinkSet().has(l.linkId);
    }

    /** Dim a node (same rule as isLinkDim). */
    isNodeDim(n: NodeView): boolean {
        if (this.hoverActive()) {
            return !this.hoverNodeSet().has(n.id);
        }
        return this.highlightActive() && !this.highlightNodeSet().has(n.id) && !this.emphasisNodeSet().has(n.id);
    }

    /** Accent a node/ribbon that is in the emphasis overlay (drawn on top of the lit base path). */
    isNodeEmphasis(n: NodeView): boolean {
        return this.emphasisNodeSet().has(n.id);
    }
    isLinkEmphasis(l: LinkView): boolean {
        return this.emphasisLinkSet().has(l.linkId);
    }
    private readonly hasEmphasisFractions = computed(() => Object.keys(this.emphasisLinkFractions()).length > 0);

    /** Width of the proportional selection band drawn over a ribbon: the ribbon's width scaled by the
     *  selected share of its circuits, with a small floor so a thin slice still reads. The band is a centered
     *  stroke, so it sits inside the base ribbon. When fractions are supplied but this ribbon has none, it
     *  carries no selected flow (e.g. the uncovered sub-band of a split ribbon) -> no band. When none are
     *  supplied at all, the whole ribbon is the band (consumers that accent without a proportion). */
    emphasisWidth(l: LinkView): number {
        const f = this.emphasisLinkFractions()[l.linkId];
        const frac = f != null ? Math.max(0, Math.min(1, f)) : this.hasEmphasisFractions() ? 0 : 1;
        return frac <= 0 ? 0 : Math.max(0.75, l.width * frac);
    }

    private readonly hasHoverFractions = computed(() => Object.keys(this.hoverLinkFractions()).length > 0);
    /** Width of the proportional hover band over a ribbon - same rule as {@link emphasisWidth}. */
    hoverWidth(l: LinkView): number {
        const f = this.hoverLinkFractions()[l.linkId];
        const frac = f != null ? Math.max(0, Math.min(1, f)) : this.hasHoverFractions() ? 0 : 1;
        return frac <= 0 ? 0 : Math.max(0.75, l.width * frac);
    }

    /** Soft-accent a node/ribbon in the hover overlay (the hovered breakdown row's path). */
    isNodeHover(n: NodeView): boolean {
        return this.hoverNodeSet().has(n.id);
    }
    isLinkHover(l: LinkView): boolean {
        return this.hoverLinkSet().has(l.linkId);
    }
    /** Truncate a label to `max` chars with an ellipsis. */
    truncLabel(s: string, max = 22): string {
        return s.length > max ? s.slice(0, max - 1) + '…' : s;
    }

    /**
     * On-screen width (px) of a node label at its rendered font, measured with a shared canvas. Used to fit
     * the left margin to the leftmost column's widest label instead of estimating from character count, which
     * over-reserves for narrow labels like numeric IPs. Falls back to a char-count estimate if canvas 2D is
     * unavailable (e.g. server-side render).
     */
    private measureLabelWidth(text: string): number {
        if (this.labelMeasureCtx === undefined) {
            const ctx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
            if (ctx) ctx.font = `600 ${NODE_LABEL_TARGET_PX}px Inter, "Helvetica Neue", Arial, sans-serif`;
            this.labelMeasureCtx = ctx;
        }
        return this.labelMeasureCtx ? this.labelMeasureCtx.measureText(text).width : text.length * LABEL_CHAR_W;
    }
    private labelMeasureCtx?: CanvasRenderingContext2D | null;

    // --- expand-pill geometry (an expandable node's name + pill sit on its leading/left side). Every size is
    // divided by displayScale so the pill renders a constant size on screen, like the labels/headers. ---
    get pillW(): number {
        return PILL_W / this.displayScale();
    }
    get pillH(): number {
        return PILL_H / this.displayScale();
    }
    /** Count-text font size in viewBox units, held constant on screen. */
    pillTextSize(): number {
        return PILL_TXT_PX / this.displayScale();
    }
    private get pillGap(): number {
        return PILL_GAP / this.displayScale();
    }
    /** x of the pill's left edge. */
    pillRectX(n: NodeView): number {
        return n.x0 - this.pillGap - this.pillW;
    }
    /** y of the pill's top edge. */
    pillRectY(n: NodeView): number {
        return n.y0 + n.h / 2 - this.pillH / 2;
    }
    /** Right-anchored x for the node's name, left of the pill. */
    expandLabelX(n: NodeView): number {
        return n.x0 - this.pillGap - this.pillW - NAME_GAP / this.displayScale();
    }
    /** x for the count text (collapsed pills only), beside the chevron. */
    pillTextX(n: NodeView): number {
        return n.x0 - this.pillGap - this.pillW / 2 + 5 / this.displayScale();
    }
    /** The chevron as a 3-point polyline: a down chevron (expanded) or a right chevron (collapsed). Always a
     *  stroked angle, never a filled caret. */
    chevronPoints(n: NodeView): string {
        const k = 1 / this.displayScale();
        const cy = n.y0 + n.h / 2;
        const s = CHEV_S * k;
        const mid = n.x0 - this.pillGap - this.pillW / 2;
        if (n.expanded) {
            return `${mid - s},${cy - s / 2} ${mid},${cy + s / 2} ${mid + s},${cy - s / 2}`;
        }
        const cx = mid - 6 * k; // left of the count
        return `${cx - s / 2},${cy - s} ${cx + s / 2},${cy} ${cx - s / 2},${cy + s}`;
    }

    // --- pan/zoom plumbing ---
    /** How far (as a fraction of the view) the chart may be dragged past its edge. This is what lets a drag
     *  move the chart even at the fitted view - without it, pan would be pinned to 0 at k=1 and dragging would
     *  feel dead - while still stopping the chart from being flung fully out of sight (most of it stays on
     *  screen, so you can always pull it back). */
    private readonly panOverscroll = 0.6;
    /** Bound the pan so the chart always stays mostly in view: content edges, plus the overscroll margin. */
    private clampView(v: { k: number; x: number; y: number }): { k: number; x: number; y: number } {
        const { width, height } = this.layout();
        const ox = width * this.panOverscroll;
        const oy = height * this.panOverscroll;
        return {
            k: v.k,
            x: Math.min(ox, Math.max(width * (1 - v.k) - ox, v.x)),
            y: Math.min(oy, Math.max(height * (1 - v.k) - oy, v.y)),
        };
    }

    /** Convert a client (screen) point to the svg's base viewBox coordinates. */
    private toViewBox(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
        const ctm = svg.getScreenCTM();
        if (!ctm) return { x: clientX, y: clientY };
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const p = pt.matrixTransform(ctm.inverse());
        return { x: p.x, y: p.y };
    }

    /** Scroll to zoom, keeping the point under the cursor fixed. */
    onWheel(event: WheelEvent): void {
        event.preventDefault();
        const svg = event.currentTarget as SVGSVGElement;
        const p = this.toViewBox(svg, event.clientX, event.clientY);
        const v = this.view();
        // wheel sensitivity: larger zooms faster per scroll notch / trackpad tick
        const k = Math.min(this.maxK, Math.max(this.minK, v.k * Math.exp(-event.deltaY * 0.004)));
        if (k === v.k) return;
        const ratio = k / v.k;
        this.view.set(this.clampView({ k, x: p.x - ratio * (p.x - v.x), y: p.y - ratio * (p.y - v.y) }));
    }

    /** Start a pan drag (primary button only). */
    onPointerDown(event: PointerEvent): void {
        if (event.button !== 0) return;
        const svg = event.currentTarget as SVGSVGElement;
        const p = this.toViewBox(svg, event.clientX, event.clientY);
        const v = this.view();
        // arm a possible pan, but do NOT capture the pointer yet: capturing here would retarget the
        // click to the svg and stop it reaching the node/ribbon/expand-pill (breaking the side panel and
        // the drill-down pills). Capture only once a real drag starts (see onPointerMove).
        this.dragStart = { px: p.x, py: p.y, vx: v.x, vy: v.y };
        this.downClient = { x: event.clientX, y: event.clientY };
        this.suppressClick = false;
    }

    /** Pan: move the content by the cursor's travel in viewBox units (scale-independent). */
    onPointerMove(event: PointerEvent): void {
        if (!this.dragStart) return;
        const svg = event.currentTarget as SVGSVGElement;
        // only begin an actual pan once the pointer has traveled past a few pixels, so a plain click (no
        // travel) stays a click and opens the side panel / toggles the pill.
        if (!this.panning()) {
            if (!this.downClient || Math.hypot(event.clientX - this.downClient.x, event.clientY - this.downClient.y) <= 4) {
                return;
            }
            this.panning.set(true);
            this.suppressClick = true; // the release will fire a click; swallow it so the pan is not a click too
            svg.setPointerCapture(event.pointerId);
        }
        const p = this.toViewBox(svg, event.clientX, event.clientY);
        this.view.set(this.clampView({
            k: this.view().k,
            x: this.dragStart.vx + (p.x - this.dragStart.px),
            y: this.dragStart.vy + (p.y - this.dragStart.py),
        }));
    }

    /** End a pan drag. When a pan happened, `suppressClick` is left set so the trailing click is swallowed; it resets on the next press. */
    onPointerUp(event: PointerEvent): void {
        if (!this.dragStart) return;
        this.dragStart = null;
        this.downClient = null;
        if (this.panning()) {
            this.panning.set(false);
            const svg = event.currentTarget as SVGSVGElement;
            if (svg.hasPointerCapture(event.pointerId)) svg.releasePointerCapture(event.pointerId);
        }
    }

    /** Reset zoom + pan to the fitted view. */
    onResetView(): void {
        this.view.set({ k: 1, x: 0, y: 0 });
    }

    // --- event plumbing ---
    onNodeClick(n: NodeView, event?: MouseEvent): void {
        if (this.suppressClick) return;
        const aggregate = !!event && (event.ctrlKey || event.metaKey);
        this.nodeClick.emit({ nodeId: n.id, stageIndex: n.stageIndex, kind: n.kind, aggregate });
    }
    onNodeHover(n: NodeView): void {
        this.nodeHover.emit({ nodeId: n.id, stageIndex: n.stageIndex, kind: n.kind });
    }
    /** The expand/collapse pill was activated. Stops the event so it does not also fire the node's click
     *  (which opens the detail panel). */
    onToggle(n: NodeView, event: Event): void {
        event.stopPropagation();
        this.nodeToggle.emit({ nodeId: n.id, stageIndex: n.stageIndex, expanded: !n.expanded });
    }
    onLinkClick(l: LinkView, event?: MouseEvent): void {
        if (this.suppressClick) return;
        const aggregate = !!event && (event.ctrlKey || event.metaKey);
        this.linkClick.emit({ linkId: l.linkId, sourceNodeId: l.sourceId, targetNodeId: l.targetId, aggregate });
    }
    onBackground(): void {
        if (this.suppressClick) return;
        this.nodeHover.emit(null);
        this.backgroundClick.emit();
    }
}
